import { LinkSimpleIcon } from "@phosphor-icons/react"
import { useEffect, useEffectEvent, useMemo, useRef } from "react"

import {
  npiMatchStatus,
  npiStatusesFor,
  useAcceptedMatches,
  type AcceptedMatchInfo,
  type NpiStatus,
} from "~/components/demand-2/shared/useAcceptedMatches"
import { useNpiVariants } from "~/components/demand-2/shared/useVariantType"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { Switch } from "~/components/design-system/components"
import { getColumnNaming } from "~/components/planning/ProductTable/utils/columnUtils"
import { useFilters } from "~/hooks/useFilters"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { Filter, FilterFieldType } from "~/utils/filter"
import { isPhasedOutVariant } from "~/utils/lifecycleUtils"

/**
 * Page-filter field for a new variant's match status. Not a column of the variant read (it comes
 * from the NPI worklist and its accepted matches), so tabs stamp it on before their page filters
 * run. Only new variants on the worklist carry one (not phased out), so a set filter keeps those
 * alone.
 */
export const MATCH_STATUS_FIELD = "npiMatchStatus"

export const matchStatusField = (kind: "fashion" | "noos"): FilterFieldType => ({
  value: MATCH_STATUS_FIELD,
  name: getColumnNaming("demand2-npi-status")?.columnChooserTitle ?? "Match status",
  type: "enum",
  icon: <LinkSimpleIcon />,
  options: npiStatusesFor(kind),
})

/**
 * Page-filter field marking a new variant the planner still has to act on before it can plan: no
 * match settled yet, or (NOOS) no start date. Stamped beside the match status, which alone
 * can't say it: a waived variant missing its date reads "No comparable".
 */
export const NEEDS_ACTION_FIELD = "npiNeedsAction"
const NEEDS_ACTION = "Needs action"

export const needsActionField: FilterFieldType = {
  value: NEEDS_ACTION_FIELD,
  name: NEEDS_ACTION,
  type: "enum",
  icon: <LinkSimpleIcon />,
  options: [NEEDS_ACTION],
}

/** Unmatched, or on NOOS still without a start date (matched or waived). */
export const npiNeedsAction = (
  npi: { startDate?: Date | null },
  status: NpiStatus,
  kind: "fashion" | "noos",
): boolean => {
  if (status === "Unmatched" || status === "Activate") return true
  return kind === "noos" && status === "No comparable" && npi.startDate == null
}

const NEEDS_ACTION_FILTER: Filter = {
  field: NEEDS_ACTION_FIELD,
  operator: "oneOf",
  value: "",
  values: [NEEDS_ACTION],
  by: "variantId",
}

export const isNeedsActionOnly = (filters: readonly Filter[]): boolean =>
  filters.some(
    (f) =>
      f.field === NEEDS_ACTION_FIELD &&
      f.operator === "oneOf" &&
      f.values?.length === 1 &&
      f.values[0] === NEEDS_ACTION,
  )

/** Replaces any needs-action filter, so the toggle and a hand-built one never stack. */
export const withNeedsActionOnly = (filters: readonly Filter[], on: boolean): Filter[] => [
  ...filters.filter((f) => f.field !== NEEDS_ACTION_FIELD),
  ...(on ? [NEEDS_ACTION_FILTER] : []),
]

/**
 * Takes the toggle's filter off when the New variants view is left. Every Assortment view and every
 * collection share the tab's filter scope, so left on it would narrow them all to new variants
 * needing action.
 */
export const useNeedsActionWhile = (active: boolean) => {
  const { filters, setFilters } = useFilters()
  const pending = useRef<ReturnType<typeof setTimeout>>(undefined)
  const clear = useEffectEvent(() => {
    if (isNeedsActionOnly(filters)) void setFilters(withNeedsActionOnly(filters, false))
  })
  useEffect(() => {
    if (!active) return
    // Deferred and cancelled on remount: StrictMode's mount, unmount, mount would clear it on arrival.
    clearTimeout(pending.current)
    return () => {
      pending.current = setTimeout(clear, 0)
    }
  }, [active])
}

/** The worklist's "Needs action" shortcut: unmatched, or missing an activation date. */
export const NeedsActionToggle = () => {
  const { filters, setFilters } = useFilters()
  return (
    <div className="flex flex-row items-center justify-between gap-2">
      <span className="label-base">Needs action</span>
      <Switch
        checked={isNeedsActionOnly(filters)}
        onCheckedChange={(checked) => void setFilters(withNeedsActionOnly(filters, checked))}
      />
    </div>
  )
}

export const npiMatchStatuses = (
  npi: readonly {
    variantId: string
    startDate?: Date | null
    ownSales?: boolean
  }[],
  matchMap: ReadonlyMap<string, AcceptedMatchInfo>,
  kind: "fashion" | "noos",
): ReadonlyMap<string, NpiStatus> =>
  new Map(npi.map((v) => [v.variantId, npiMatchStatus(v, matchMap.get(v.variantId), kind)]))

/** What `stampMatchStatus` stamps: each worklist variant's status, and those needing action. */
export type MatchStatusStamp = {
  statuses: ReadonlyMap<string, NpiStatus>
  needsAction: ReadonlySet<string>
}

export const matchStatusStamp = (
  npi: readonly {
    variantId: string
    startDate?: Date | null
    ownSales?: boolean
  }[],
  matchMap: ReadonlyMap<string, AcceptedMatchInfo>,
  kind: "fashion" | "noos",
): MatchStatusStamp => {
  const rows = npi.map((v) => ({ v, status: npiMatchStatus(v, matchMap.get(v.variantId), kind) }))
  return {
    statuses: new Map(rows.map(({ v, status }) => [v.variantId, status])),
    needsAction: new Set(
      rows.filter(({ v, status }) => npiNeedsAction(v, status, kind)).map(({ v }) => v.variantId),
    ),
  }
}

/**
 * The stamp, or null while no match-status or needs-action filter is set and the caller doesn't
 * need it (`alsoNeeded`): the worklist reads are only worth making when something reads them.
 */
export function useMatchStatusesForFilter(alsoNeeded = false): MatchStatusStamp | null {
  const { filters } = useFilters()
  const on =
    alsoNeeded ||
    filters.some((f) => f.field === MATCH_STATUS_FIELD || f.field === NEEDS_ACTION_FIELD)
  const { collectionIds, kind: scopeKind } = useDemand2Scope()
  const kind = scopeKind ?? "fashion"
  const { npiQ } = useNpiVariants(collectionIds, kind, on)
  const matchMap = useAcceptedMatches(kind, on ? collectionIds : [])
  return useMemo(
    () => (on && npiQ.data ? matchStatusStamp(npiQ.data, matchMap, kind) : null),
    [on, npiQ.data, matchMap, kind],
  )
}

/** Passes `variants` through untouched when `stamp` is null (no match-status filter set). */
export const stampMatchStatus = <V extends IVariantSummary>(
  variants: V[],
  stamp: MatchStatusStamp | null,
): V[] =>
  stamp
    ? variants.map((v) => {
        const status = stamp.statuses.get(v.variantId)
        if (!status || isPhasedOutVariant(v)) return v
        return {
          ...v,
          [MATCH_STATUS_FIELD]: status,
          ...(stamp.needsAction.has(v.variantId) ? { [NEEDS_ACTION_FIELD]: NEEDS_ACTION } : {}),
        }
      })
    : variants
